import { openDB } from 'idb';
export var EntityType;
(function (EntityType) {
    EntityType["TENANT"] = "tenant";
    EntityType["CLUSTER"] = "cluster";
    EntityType["TOPIC"] = "topic";
})(EntityType || (EntityType = {}));
// Check if IndexedDB is available
const isIndexedDBAvailable = () => {
    try {
        return typeof indexedDB !== 'undefined';
    }
    catch (e) {
        return false;
    }
};
export class CacheService {
    static async initDB() {
        if (!isIndexedDBAvailable()) {
            console.warn('CacheService: IndexedDB is not available');
            return null;
        }
        if (this.db) {
            return this.db;
        }
        try {
            this.db = await openDB(this.dbName, this.version, {
                upgrade(db, oldVersion, newVersion) {
                    // Create tenant store
                    if (!db.objectStoreNames.contains(EntityType.TENANT)) {
                        db.createObjectStore(EntityType.TENANT, { keyPath: 'id' });
                    }
                    // Create topics store with composite key
                    if (!db.objectStoreNames.contains('topics')) {
                        const topicsStore = db.createObjectStore('topics', { keyPath: ['tenantId', 'id'] });
                        topicsStore.createIndex('byTenant', 'tenantId');
                        topicsStore.createIndex('byId', 'id');
                    }
                    // Create clusters store with composite key
                    if (!db.objectStoreNames.contains('clusters')) {
                        const clustersStore = db.createObjectStore('clusters', { keyPath: ['tenantId', 'id'] });
                        clustersStore.createIndex('byTenant', 'tenantId');
                        clustersStore.createIndex('byId', 'id');
                    }
                },
            });
            return this.db;
        }
        catch (error) {
            console.error('CacheService: Failed to initialize IndexedDB', error);
            this.db = null;
            throw error;
        }
    }
    static async deleteDatabase() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.deleteDatabase(this.dbName);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = null;
                resolve();
            };
        });
    }
    static getStoreName(type) {
        return type === EntityType.TENANT ? type : `${type}s`;
    }
    static async getEntity(type, id, tenantId) {
        try {
            const db = await this.initDB();
            if (!db)
                return null;
            const storeName = this.getStoreName(type);
            if (type === EntityType.TENANT) {
                return await db.get(storeName, id);
            }
            // Try to get by composite key first if tenantId is provided
            if (tenantId) {
                return await db.get(storeName, [tenantId, id]);
            }
            // If no tenantId, try to get by id index
            const entities = await db.getAllFromIndex(storeName, 'byId', id);
            return entities[0] || null;
        }
        catch (error) {
            console.warn(`CacheService: Failed to get ${type} with id ${id}`, error);
            return null;
        }
    }
    static async setEntity(type, entity, tenantId) {
        try {
            const db = await this.initDB();
            if (!db)
                return null;
            const storeName = this.getStoreName(type);
            if (type === EntityType.TENANT) {
                await db.put(storeName, entity);
            }
            else {
                await db.put(storeName, Object.assign(Object.assign({}, entity), { tenantId }));
            }
            return entity;
        }
        catch (error) {
            console.warn(`CacheService: Failed to set ${type}`, error);
            return null;
        }
    }
    static async setEntities(type, entities, tenantId) {
        try {
            const db = await this.initDB();
            if (!db)
                return [];
            const storeName = this.getStoreName(type);
            // remove all entities
            await this.removeAllEntities(type, tenantId);
            // save new ones
            if (type === EntityType.TENANT) {
                await Promise.all(entities.map(entity => db.put(storeName, entity)));
            }
            else {
                await Promise.all(entities.map(entity => {
                    var _a;
                    return db.put(storeName, Object.assign(Object.assign({}, entity), { tenantId: (_a = entity.tenantId) !== null && _a !== void 0 ? _a : tenantId }));
                }));
            }
            return entities;
        }
        catch (error) {
            console.warn(`CacheService: Failed to set multiple ${type}s`, error);
            return [];
        }
    }
    static async removeEntity(type, id, tenantId) {
        try {
            const db = await this.initDB();
            if (!db)
                return;
            const storeName = this.getStoreName(type);
            if (type === EntityType.TENANT) {
                await db.delete(storeName, id);
            }
            else {
                await db.delete(storeName, [tenantId, id]);
            }
        }
        catch (error) {
            console.warn(`CacheService: Failed to remove ${type} with id ${id}`, error);
        }
    }
    static async removeAllEntities(type, tenantId) {
        try {
            const db = await this.initDB();
            if (!db)
                return;
            const storeName = this.getStoreName(type);
            if (type === EntityType.TENANT) {
                // Clear all tenants
                await db.clear(storeName);
            }
            else {
                // Get all entities for the given tenant
                const entities = await db.getAllFromIndex(storeName, 'byTenant', tenantId);
                // Remove each entity
                await Promise.all(entities.map(entity => db.delete(storeName, [tenantId, entity.id])));
            }
        }
        catch (error) {
            console.warn(`CacheService: Failed to remove all ${type}s`, error);
        }
    }
    static async clearEntityType(type, tenantId) {
        try {
            const db = await this.initDB();
            if (!db)
                return;
            const storeName = this.getStoreName(type);
            await db.clear(storeName);
        }
        catch (error) {
            console.warn(`CacheService: Failed to clear ${type}`, error);
        }
    }
    static async getAllEntities(type, tenantId) {
        try {
            const db = await this.initDB();
            if (!db)
                return [];
            const storeName = this.getStoreName(type);
            if (type === EntityType.TENANT) {
                const tenants = await db.getAll(storeName);
                return tenants;
            }
            const entities = await db.getAllFromIndex(storeName, 'byTenant', tenantId);
            return entities;
        }
        catch (error) {
            console.warn(`CacheService: Failed to get all ${type}s`, error);
            return [];
        }
    }
    static async clearAll() {
        try {
            const db = await this.initDB();
            if (!db)
                return;
            // Clear all stores
            await Promise.all(Array.from(db.objectStoreNames).map(storeName => db.clear(storeName)));
        }
        catch (error) {
            console.warn('CacheService: Failed to clear all data', error);
        }
    }
    static async clearTenantData(tenantId) {
        try {
            const db = await this.initDB();
            if (!db)
                return;
            // Clear all tenant-specific stores
            await Promise.all(Object.values(EntityType)
                .filter(type => type !== EntityType.TENANT) // Skip tenant store
                .map(type => this.clearEntityType(type, tenantId)));
        }
        catch (error) {
            console.warn(`CacheService: Failed to clear data for tenant ${tenantId}`, error);
        }
    }
}
CacheService.dbName = 'app-cache';
CacheService.version = 2; // Increment version for schema change
CacheService.db = null;
